import React, { forwardRef, ChangeEvent, Ref } from "react";
import FormField from "../formField/FormField";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { useBackgroundColor } from "../../../utils";

export interface InputProps {
  type?: "text" | "number";
  label: string;
  placeholder?: string;
  defaultValue?: string | number;
  hideLabel?: boolean;
  required?: boolean;
  id: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  invalid?: boolean;
  valid?: boolean;
  errorMessage?: string;
}

interface StyledInputProps {
  invalid?: boolean;
  valid?: boolean;
}

const StyledInput = styled.input<StyledInputProps>`
  ${({ theme: { space, colors, borderRadii }, invalid, valid }) => css`
    border: 2px solid
      ${valid ? colors.green : invalid ? colors.red : colors.darkBlue};
    ${useBackgroundColor("white")}
    padding: ${space.xxSmall} ${space.xSmall};
    border-radius: ${borderRadii.small};
    width: 100%;
    transition: all 0.2s ease-in-out;

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }

    /* Works for Firefox */
    &[type="number"] {
      -moz-appearance: textfield;
    }

    // Hover

    &:hover {
      box-shadow: 0 0 0 1px ${colors.darkBlue};
      opacity: 0.9;
    }
  `};
`;

export const Input = forwardRef(
  (
    {
      label,
      id,
      defaultValue,
      placeholder,
      hideLabel,
      required,
      type,
      onChange,
      invalid,
      valid,
      errorMessage,
      ...rest
    }: InputProps,
    ref: Ref<HTMLInputElement>
  ) => {
    return (
      <FormField
        label={label}
        htmlFor={id}
        hideLabel={hideLabel}
        invalid={invalid}
        errorMesage={errorMessage || "This field is required"}
      >
        <StyledInput
          id={id}
          defaultValue={defaultValue}
          type={type}
          required={required}
          onChange={onChange}
          ref={ref}
          invalid={invalid}
          valid={valid}
          aria-invalid={invalid} // Add aria-invalid attribute
          aria-describedby={`${id}-error`} // Reference to the error message
          {...rest}
        />
      </FormField>
    );
  }
);

export default Input;
